import {InfoOutlineIcon} from '@sanity/icons'
import {Card, Flex, Radio, Stack, Text, TextInput} from '@sanity/ui'
import {type ChangeEvent, useState} from 'react'

import {type PutResourceBudgetRequestParams} from '../../../../data/customers/useBudgets'
import {Dialog} from '../../primitives/dialog'

export type EditBudgetDialogProps = {
  featureName: string
  defaultCap: number
  currentCustomCap: number | undefined | null
  currentSpend: number
  currentCapSetting: 'default' | 'unlimited' | 'custom'
  onSetCap: (data: PutResourceBudgetRequestParams) => void
  onCancel: () => void
  submitting?: boolean | null
}

export const EditBudgetDialog: React.FC<EditBudgetDialogProps> = ({
  featureName,
  currentCapSetting,
  currentCustomCap,
  currentSpend,
  defaultCap,
  onCancel,
  onSetCap,
  submitting,
}) => {
  const [capSelection, setCapSelection] = useState(currentCapSetting || 'default')

  const handleCapSelection = (e: ChangeEvent<HTMLInputElement>) => {
    setCapSelection(e.currentTarget.value as any)
  }

  const [customCap, setCustomCap] = useState(currentCustomCap || 0)

  const capValues = {default: defaultCap, custom: customCap, unlimited: null} as const

  const handleSave = () => {
    switch (capSelection) {
      case 'default':
        onSetCap({maxAmount: defaultCap})
        break
      case 'unlimited':
        onSetCap({maxAmount: null})
        break
      case 'custom':
        onSetCap({maxAmount: customCap})
    }
  }

  const showSpendWarning =
    (capSelection === 'custom' && customCap && customCap < currentSpend) ||
    (capSelection === 'default' && defaultCap < currentSpend)

  return (
    <Dialog
      header={`Edit ${featureName.toLowerCase()} cap`}
      width={1}
      id="edit-budget-dialog"
      cancelButton={{
        onClick: onCancel,
      }}
      confirmButton={{
        text: 'Save',
        onClick: handleSave,
        loading: submitting || false,
      }}
      onClickOutside={onCancel}
    >
      <Stack space={5}>
        <Text muted>
          Your intended cap for your organization. Your actual costs may exceed this cap based on
          usage.
        </Text>

        <Stack space={3}>
          <Flex align={'center'} gap={3}>
            <Radio
              name="cap-selection"
              onChange={handleCapSelection}
              checked={capSelection === 'default'}
              value={'default'}
            />
            <Text>Default: ${defaultCap} worth of usage per month (free)</Text>
          </Flex>

          <Flex align={'center'} gap={3}>
            <Radio
              name="cap-selection"
              onChange={handleCapSelection}
              checked={capSelection === 'unlimited'}
              value={'unlimited'}
            />
            <Text>Unlimited</Text>
          </Flex>

          <Flex align={'center'} gap={3}>
            <Radio
              name="cap-selection"
              onChange={handleCapSelection}
              checked={capSelection === 'custom'}
              value={'custom'}
            />
            <Text>Custom monthly cap</Text>
          </Flex>

          {capSelection === 'custom' && (
            <Card border paddingX={2}>
              <Flex align={'center'}>
                <Text>$</Text>

                <TextInput
                  border={false}
                  __unstable_disableFocusRing
                  autoFocus
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={customCap}
                  onChange={(e) => setCustomCap(Number(e.currentTarget.value))}
                />
              </Flex>
            </Card>
          )}

          {showSpendWarning && (
            <Card tone={'caution'} padding={4} radius={3}>
              <Flex align={'center'} gap={3}>
                <Text size={1}>
                  <InfoOutlineIcon />
                </Text>
                <Text size={1}>
                  You will be charged ${currentSpend} this month and then ${capValues[capSelection]}{' '}
                  from next month onwards
                </Text>
              </Flex>
            </Card>
          )}
        </Stack>
      </Stack>
    </Dialog>
  )
}
